// SixDetailServiceImpl.ts
import { SixDetailService } from '@/services/abstract/SixDetailService';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { sixEndpoints } from '@/api/endpoints';
import type {
  ApiResponseDTO,
  CreateFolderResponseDTO,
  DeleteFileResponseDTO,
  FetchFilesParams,
  MakeFileAvailableToHAPPayloadDTO,
  MakeFileAvailableToHAPResultDTO,
  MoveItemsPayloadDTO,
  ParticipantsPayloadDTO,
  SixActivityDTO,
  SixFilesApiResponse,
  SixFileDTO,
  SixSummaryDTO,
  UpdateParticipantsResponseDTO,
} from '@/types/SixDetailDTOs';
import type { SixDetailFilterKey } from '@/types/SixDetail.types';
import type { UpdateManageAccessPayload } from '@/types/manage-access.types';
import type { SixFileFavoriteDTO } from '@/types/SixCatalogDTOs';
import type { DeletedFileEntry, DeletedFilesResponse } from '@/types/poc/deletedFiles';

type MaybeWrapped<T> = T | ApiResponseDTO<T>;

function isWrappedResponse<T>(value: MaybeWrapped<T>): value is ApiResponseDTO<T> {
  return typeof value === 'object' && value !== null && 'payload' in value;
}

function unwrapPayload<T>(value: MaybeWrapped<T>): T {
  return isWrappedResponse(value) ? value.payload : value;
}

/**
 * Implementation of Six detail service for managing individual Six workspaces.
 *
 * Works with any FetcherService implementation:
 * - JsonFetcherService for mock/local development
 * - HttpFetcherService for production API calls
 *
 * File filtering and CSV export are handled client-side to remain
 * fetcher-agnostic. When the backend supports server-side filtering,
 * the fetchFiles method can be updated to delegate to the backend.
 *
 * @see SixDetailService
 */
export class SixDetailServiceImpl extends SixDetailService {
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  async getMeta(
    identifier: string,
    filters?: readonly SixDetailFilterKey[]
  ): Promise<SixSummaryDTO> {
    const response = await this.fetcher.get<MaybeWrapped<SixSummaryDTO>>(
      sixEndpoints.getSix(identifier, { filter: filters })
    );
    return unwrapPayload(response);
  }

  /**
   * Fetches files and folders for a Six workspace using backend filtering/sorting.
   */
  async fetchFiles(identifier: string, params?: FetchFilesParams): Promise<SixFilesApiResponse> {
    const url = sixEndpoints.getSixFiles(identifier, {
      filter: params?.filters,
      search: params?.search,
      path: params?.path,
      sortKey: params?.sortKey,
      sortDir: params?.sortDir,
    });
    const response = await this.fetcher.get<MaybeWrapped<SixFilesApiResponse>>(url);
    return unwrapPayload(response);
  }

  async uploadFile(id: string, file: File): Promise<SixFileDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return await this.fetcher.post<SixFileDTO>(sixEndpoints.uploadSixFiles(id), formData);
  }

  /**
   * Hands an already Helia-stored encrypted file off to the backend/HAP path.
   *
   * The endpoint returns 202 `{ commandId, fileId, resourceLink }`; the fetcher
   * transparently normalizes that into a command-status poll and resolves with
   * the command `meta` (`{ fileId }`) on success, or rejects on failure/timeout.
   */
  async makeFileAvailableToHAP(
    sixId: string,
    payload: MakeFileAvailableToHAPPayloadDTO
  ): Promise<MakeFileAvailableToHAPResultDTO> {
    return await this.fetcher.post<MakeFileAvailableToHAPResultDTO>(
      sixEndpoints.makeFileAvailableToHap(sixId),
      payload
    );
  }

  async createFolder(
    id: string,
    folderName: string,
    folderPath: string
  ): Promise<CreateFolderResponseDTO> {
    return await this.fetcher.post<CreateFolderResponseDTO>(sixEndpoints.createSixFolder(id), {
      folderPath,
      folderName,
    });
  }

  async deleteFolder(sixId: string, folderPath: string): Promise<DeleteFileResponseDTO> {
    return await this.fetcher.del<DeleteFileResponseDTO>(sixEndpoints.deleteFolder(sixId), {
      folderPath,
    });
  }

  async fetchActivity(id: string): Promise<SixActivityDTO[]> {
    const response = await this.fetcher.get<MaybeWrapped<SixActivityDTO[]>>(
      sixEndpoints.getSixActivity(id)
    );

    return unwrapPayload(response);
  }

  /**
   * Exports the activity log as a CSV file.
   *
   * Fetches activity data as JSON and converts to CSV client-side
   * so the method works with any fetcher (no blob endpoint required).
   */
  async exportActivityCsv(id: string): Promise<Blob> {
    const activities = await this.fetchActivity(id);
    const csvHeader = 'ID,Action,Description,User Name,Timestamp';
    const csvRows = activities.map((activity) => {
      const description = activity.description.join(' ');
      return `${activity.id},${activity.action},"${description}",${activity.userName},${activity.timestamp}`;
    });
    const csvContent = [csvHeader, ...csvRows].join('\n');
    return new Blob([csvContent], { type: 'text/csv' });
  }

  async fetchParticipants(id: string): Promise<ParticipantsPayloadDTO> {
    const response = await this.fetcher.get<MaybeWrapped<ParticipantsPayloadDTO>>(
      sixEndpoints.getSixParticipants(id)
    );
    return unwrapPayload(response);
  }

  async updateSixParticipants(
    sixId: string,
    payload: UpdateManageAccessPayload
  ): Promise<UpdateParticipantsResponseDTO> {
    return await this.fetcher.patch<UpdateParticipantsResponseDTO>(
      sixEndpoints.updateSixParticipants(sixId),
      payload
    );
  }

  async deleteFile(sixId: string, fileId: string): Promise<DeleteFileResponseDTO> {
    return await this.fetcher.del<DeleteFileResponseDTO>(sixEndpoints.deleteSixFile(sixId, fileId));
  }

  async toggleFileFavorite(sixId: string, fileId: string): Promise<SixFileFavoriteDTO> {
    return await this.fetcher.post(sixEndpoints.toggleFileFavorite(sixId, fileId), {});
  }

  async moveItems(sixId: string, payload: MoveItemsPayloadDTO): Promise<void> {
    await this.fetcher.post(sixEndpoints.moveItems(sixId), payload);
  }

  async markFileDownloaded(sixId: string, fileId: string): Promise<void> {
    await this.fetcher.post(sixEndpoints.markFileDownloaded(sixId, fileId), {});
  }

  async fetchDeletedFiles(): Promise<DeletedFileEntry[]> {
    const response = await this.fetcher.get<MaybeWrapped<DeletedFilesResponse>>(
      sixEndpoints.getDeletedFiles()
    );
    return unwrapPayload(response).deletedFiles;
  }
}
